import { useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useStrategyGuidance } from '../hooks/useStrategyGuidance';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { ConfirmModal } from '../components/ConfirmModal';
import { MobileBuildingsShell } from '../components/buildings/MobileBuildingsShell';
import { DesktopBuildingsShell } from '../components/buildings/DesktopBuildingsShell';
import { COLORS } from '../constants/theme';
import { BUILDING_MAP } from '../constants/buildings';
import { computeDeficit, resolveTargetQty } from '../components/buildings/buildingsPercent';
import { parseBuildingsTab } from '../constants/buildingsTabs';
import { formatNumber } from '../utils/format';
import { computeAutoFill } from '../utils/buildAutoPopulate';
import { api } from '../api/client';
import type { Province, Construction } from '../types/province';
import type { ActiveStrategyDto } from '../types/strategy';
import type { BuildRequest, BuildResult } from '../types/build';

const HIGHLIGHT_DURATION_MS = 1200;

export type BuildingsTab = 'build' | 'raze' | 'overview';

/**
 * SF-132: named, fully-typed props shared by MobileBuildingsShell/DesktopBuildingsShell —
 * replaces v1.0's untyped `{...sharedProps}` spread. Deliberate deviation from ThroneRoom's
 * shell-prop pattern (shells there take only `{ province }` and call shared hooks
 * independently): Buildings has ~10 pieces of mutable draft/submission state that must be one
 * shared instance across a breakpoint flip, not duplicated per shell.
 */
export interface BuildingsShellProps {
  province: Province;
  activeStrategy: ActiveStrategyDto | null;
  strategyLoading: boolean;
  activeTab: BuildingsTab;
  setActiveTab: (tab: BuildingsTab) => void;
  buildQty: Record<string, number>;
  isAccelerated: boolean;
  setIsAccelerated: (value: boolean) => void;
  isSubmitting: boolean;
  submitError: string | null;
  successMessage: string | null;
  highlighted: Record<string, boolean>;
  handleBuildQtyChange: (buildingId: string, value: number) => void;
  handleFillDeficit: (buildingId: string) => void;
  handleSetTargetAcres: (buildingId: string, targetAcres: number) => void;
  /** SF-190 Component 6: single derived aria-disabled+aria-describedby reason for "⚡ Fill
   *  Strategy," replacing native `disabled` (matching Raze's mirror button's convention). `null`
   *  means the button is enabled. Additive on `DesktopBuildingsShell.tsx` — not required to
   *  consume this pass. */
  fillStrategyBlockedReason: string | null;
  handleOptimizeAll: () => void;
  handleClearAll: () => void;
  handleSubmitClick: () => void;
  bulkMutationVersion: number;
  totalLand: number;
  barren: number;
  buildings: Record<string, number>;
  construction: Construction[];
  inProgress: Record<string, number>;
  totalBuildings: number;
  totalBuildQty: number;
  remainingBarren: number;
  constructionTime: number;
  costPerAcre: number;
  totalCost: number;
  be: number;
  worldId: string | null;
  provinceId: string | null;
  refresh: () => Promise<void>;
}

export function Buildings() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, refresh } = useProvince();
  const { activeStrategy, isLoading: strategyLoading } = useStrategyGuidance(worldId, provinceId);
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const [buildQty, setBuildQty] = useState<Record<string, number>>({});
  const [isAccelerated, setIsAccelerated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [highlighted, setHighlighted] = useState<Record<string, boolean>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  // SF-160: the URL is the source of truth on every tab switch — a refresh or browser back
  // reproduces the same tab, and the `tab` param is omitted entirely for the default ('build') so
  // the common case still renders a clean /buildings URL.
  const [activeTab, setActiveTabState] = useState<BuildingsTab>(
    () => parseBuildingsTab(searchParams.get('tab'))
  );
  const setActiveTab = useCallback(
    (tab: BuildingsTab) => {
      setActiveTabState(tab);
      setSearchParams(tab === 'build' ? {} : { tab }, { replace: true });
    },
    [setSearchParams]
  );
  // SF-143: bumped on every external bulk mutation of buildQty (Clear, Fill Strategy,
  // submit-success) so MobileBuildingsShell's expanded-row stepper can collapse rather than
  // silently resurrect/rewrite stale data on its next tap.
  const [bulkMutationVersion, setBulkMutationVersion] = useState(0);

  // useRef to track highlight timeouts — cleared on unmount
  const highlightTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearHighlight = useCallback((buildingId: string) => {
    if (highlightTimers.current[buildingId]) {
      clearTimeout(highlightTimers.current[buildingId]);
    }
    highlightTimers.current[buildingId] = setTimeout(() => {
      setHighlighted((prev) => ({ ...prev, [buildingId]: false }));
    }, HIGHLIGHT_DURATION_MS);
    setHighlighted((prev) => ({ ...prev, [buildingId]: true }));
  }, []);

  // SF-132 Decision 5: mobile's sticky bottom bar unmounts on success (buildQty clears), which
  // would delete a page-local success message in the same render — so mobile routes feedback
  // through the app-wide toast system instead. Desktop's existing inline messages have no
  // equivalent unmount race, so they're left exactly as before.
  const reportError = useCallback(
    (message: string) => {
      if (platform === 'mobile') {
        addToast(message, 'failure');
      } else {
        setSubmitError(message);
      }
    },
    [platform, addToast]
  );

  const reportSuccess = useCallback(
    (message: string) => {
      if (platform === 'mobile') {
        addToast(message, 'success');
      } else {
        setSuccessMessage(message);
      }
    },
    [platform, addToast]
  );

  // Derived values
  const totalLand = province?.territory.totals.acres ?? 0;
  const barren = province?.territory.totals.barren ?? 0;
  const buildings = province?.territory.buildings ?? {};
  const construction = province?.territory.construction ?? [];
  const be = province?.economy.buildingEfficiency ?? 1.0;

  const inProgress = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of construction) {
      map[c.building] = (map[c.building] ?? 0) + c.acres;
    }
    return map;
  }, [construction]);

  const totalBuildings = useMemo(
    () => Object.values(buildings).reduce((sum, n) => sum + (n || 0), 0),
    [buildings]
  );

  const totalBuildQty = useMemo(
    () => Object.values(buildQty).reduce((sum, n) => sum + n, 0),
    [buildQty]
  );

  const remainingBarren = useMemo(() => barren - totalBuildQty, [barren, totalBuildQty]);

  const baseConstructionTime = province?.territory.constructionTime ?? 0;
  const baseCostPerAcre = province?.territory.constructionCostPerAcre ?? 0;
  const constructionTime = isAccelerated ? Math.ceil(baseConstructionTime / 2) : baseConstructionTime;
  const costPerAcre = isAccelerated ? baseCostPerAcre * 2 : baseCostPerAcre;
  const totalCost = totalBuildQty * costPerAcre;

  const handleBuildQtyChange = useCallback((buildingId: string, value: number) => {
    setBuildQty((prev) => {
      const otherTotal = Object.entries(prev)
        .filter(([id]) => id !== buildingId)
        .reduce((sum, [, qty]) => sum + qty, 0);
      const max = barren - otherTotal;
      return { ...prev, [buildingId]: Math.min(Math.max(0, value), max) };
    });
  }, [barren]);

  // Desktop's fill-to-strategy-target button — unchanged behavior, still percent-driven via
  // computeDeficit. The mobile stepper's live write-through uses handleSetTargetAcres instead
  // (SF-143); its targetPctOverride param is gone since the mobile chip that was its only caller
  // with an override no longer exists.
  const handleFillDeficit = useCallback(
    (buildingId: string) => {
      const targetPct = activeStrategy?.settings?.buildings?.[buildingId] ?? 0;
      if (targetPct <= 0) return;

      const owned = buildings[buildingId] ?? 0;
      const inProg = inProgress[buildingId] ?? 0;
      let filled = false;

      setBuildQty((prev) => {
        const otherTotal = Object.entries(prev)
          .filter(([id]) => id !== buildingId)
          .reduce((sum, [, qty]) => sum + qty, 0);
        const rowRemainingBarren = barren - otherTotal;
        const { deficit, fillQty } = computeDeficit({
          targetPct,
          totalLand,
          owned,
          inProgress: inProg,
          remainingBarren: rowRemainingBarren,
        });
        if (deficit <= 0) return prev;
        filled = true;
        return { ...prev, [buildingId]: fillQty };
      });
      if (filled) clearHighlight(buildingId);
    },
    [activeStrategy, totalLand, buildings, inProgress, barren, clearHighlight]
  );

  // SF-143: acres-driven write-through backing the mobile stepper's live-commit behavior. Every
  // +/- tap or chip tap on mobile calls this directly — no separate per-row confirm step.
  const handleSetTargetAcres = useCallback(
    (buildingId: string, targetAcres: number) => {
      const owned = buildings[buildingId] ?? 0;
      const inProg = inProgress[buildingId] ?? 0;

      setBuildQty((prev) => {
        const otherTotal = Object.entries(prev)
          .filter(([id]) => id !== buildingId)
          .reduce((sum, [, qty]) => sum + qty, 0);
        const rowRemainingBarren = Math.max(0, barren - otherTotal);
        const { newQty } = resolveTargetQty({ targetAcres, owned, inProgress: inProg, remainingBarren: rowRemainingBarren });
        if (newQty <= 0) {
          const rest = { ...prev };
          delete rest[buildingId];
          return rest;
        }
        return { ...prev, [buildingId]: newQty };
      });
    },
    [buildings, inProgress, barren]
  );

  // SF-190 Component 6: the pre-existing !activeStrategy?.settings disabled check didn't go one
  // level deeper — a strategy with Military/Science settings but zero building targets rendered
  // the button enabled and clickable, and handleOptimizeAll silently no-op'd (guarded by the same
  // !activeStrategy?.settings?.buildings check below). One derived reason string drives both the
  // visible caption and the aria-describedby text, so there's exactly one source of copy.
  const fillStrategyBlockedReason: string | null = !activeStrategy?.settings
    ? 'Set a Strategy first to enable Fill Strategy.'
    : Object.values(activeStrategy.settings.buildings).every((v) => !v || v <= 0)
      ? 'Set building targets in your Strategy first.'
      : null;

  const handleOptimizeAll = () => {
    if (!activeStrategy?.settings?.buildings) return;
    const result = computeAutoFill(
      activeStrategy.settings.buildings,
      totalLand,
      buildings as Record<string, number>,
      inProgress,
      barren
    );
    setBuildQty(result.quantities);
    // Highlight all filled rows
    for (const buildingId of Object.keys(result.quantities)) {
      clearHighlight(buildingId);
    }
    setBulkMutationVersion((v) => v + 1);
  };

  const handleClearAll = () => {
    setBuildQty({});
    setSubmitError(null);
    setSuccessMessage(null);
    setBulkMutationVersion((v) => v + 1);
  };

  const handleSubmitClick = () => {
    setSubmitError(null);
    setSuccessMessage(null);

    if (totalBuildQty === 0) {
      reportError('Please enter at least one building to construct');
      return;
    }
    if (totalBuildQty > barren) {
      reportError(`Total exceeds barren land (${totalBuildQty} > ${barren})`);
      return;
    }
    if (totalBuildQty > 10) {
      setShowConfirmModal(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const buildingsRequest: Record<string, number> = {};
      Object.entries(buildQty).forEach(([id, qty]) => {
        if (qty > 0) buildingsRequest[id] = qty;
      });

      const request: BuildRequest = { buildings: buildingsRequest, accelerated: isAccelerated };
      const response = await api.post<BuildResult>(
        `/api/worlds/${worldId}/provinces/${provinceId}/build`,
        request
      );

      if (response.error) {
        reportError(response.error);
      } else if (response.data) {
        const result = response.data;
        const accelText = result.accelerated ? ' (accelerated)' : '';
        reportSuccess(
          `Construction started: ${result.totalAcresBuilt} acres for ${formatNumber(result.constructionCost)} gold. Completes in ${result.daysUntilCompleted} days${accelText}.`
        );
        setBuildQty({});
        setBulkMutationVersion((v) => v + 1);
        await refresh();
      }
    } catch {
      reportError('Failed to submit build order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ backgroundColor: COLORS.bg }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province) {
    return (
      <div className="min-h-screen px-4 py-8" style={{ backgroundColor: COLORS.bg }}>
        <ErrorMessage message={error || 'Failed to load province'} />
      </div>
    );
  }

  const buildingConfirmList = Object.entries(buildQty)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const building = BUILDING_MAP[id];
      return building ? `${qty} ${building.name}` : '';
    })
    .filter(Boolean);

  const shellProps: BuildingsShellProps = {
    province,
    activeStrategy,
    strategyLoading,
    activeTab,
    setActiveTab,
    buildQty,
    isAccelerated,
    setIsAccelerated,
    isSubmitting,
    submitError,
    successMessage,
    highlighted,
    handleBuildQtyChange,
    handleFillDeficit,
    handleSetTargetAcres,
    fillStrategyBlockedReason,
    handleOptimizeAll,
    handleClearAll,
    handleSubmitClick,
    bulkMutationVersion,
    totalLand,
    barren,
    buildings,
    construction,
    inProgress,
    totalBuildings,
    totalBuildQty,
    remainingBarren,
    constructionTime,
    costPerAcre,
    totalCost,
    be,
    worldId,
    provinceId,
    refresh,
  };

  return (
    <>
      {platform === 'mobile' ? <MobileBuildingsShell {...shellProps} /> : <DesktopBuildingsShell {...shellProps} />}

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleSubmit}
        onCancel={() => setShowConfirmModal(false)}
        title="Confirm Construction"
        confirmText="Confirm Construction"
      >
        <div>
          <p className="mb-3">You are about to construct {totalBuildQty} buildings:</p>
          <ul className="list-disc list-inside mb-3 space-y-1 text-sm">
            {buildingConfirmList.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
          <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
            This will use {totalBuildQty} acres of barren land.
          </p>
        </div>
      </ConfirmModal>
    </>
  );
}
